import { CalendarIcon, Clock, MapPin, Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { FamilyEvent, useEvents } from '@/hooks/useEvents';
import { FamilyMember } from '@/hooks/useFamily';
import { PersonName } from '@/components/PersonName';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import React, { useState } from 'react';

interface EventItemProps {
  event: FamilyEvent;
  familyMembers?: FamilyMember[];
  onClick?: () => void;
  compact?: boolean;
  showActions?: boolean;
  onDeleted?: (id: string) => void;
}

export const EventItem = ({ event, familyMembers = [], onClick, compact = false, showActions = true, onDeleted }: EventItemProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { deleteEvent } = useEvents(event.family_id);
  const { openModal } = useModalNavigation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEventClick = (e: React.MouseEvent) => {
    // Prevent event bubbling if clicked on dropdown menu
    if ((e.target as HTMLElement).closest('[role="menuitem"]') || 
        (e.target as HTMLElement).closest('button[data-state]')) {
      return;
    }
    openModal('viewEvent', { id: event.id });
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    openModal('editEvent', { id: event.id });
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteEvent(event.id);
      toast({
        title: "Événement supprimé",
        description: "L'événement a été supprimé avec succès.",
      });
      onDeleted?.(event.id);
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer l'événement.",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };
  const getEventTypeConfig = (type: string) => {
    switch (type) {
      case 'family':
        return { label: '👨‍👩‍👧‍👦 Famille', color: 'bg-purple-500 text-white' };
      case 'personal':
        return { label: '👤 Personnel', color: 'bg-blue-500 text-white' };
      case 'appointment':
        return { label: '👨‍⚕️ RDV', color: 'bg-green-500 text-white' };
      case 'birthday':
        return { label: '🎂 Anniversaire', color: 'bg-pink-500 text-white' };
      case 'holiday':
        return { label: '🎉 Vacances', color: 'bg-orange-500 text-white' };
      default:
        return { label: 'Événement', color: 'bg-gray-500 text-white' };
    }
  };

  const getCreator = () => {
    return familyMembers.find(member => 
      member.profile_id === event.created_by
    );
  };

  const creator = getCreator();
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  const eventConfig = getEventTypeConfig(event.event_type);

  if (compact) {
    return (
      <>
        <div
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border border-panel bg-panel hover:bg-panel-hover cursor-pointer transition-all relative group"
          )}
          onClick={handleEventClick}
        >
          <div className="flex flex-col items-center min-w-[50px]">
            <div className="text-xs text-muted-foreground">
              {startDate.toLocaleDateString('fr-FR', { weekday: 'short' }).toUpperCase()}
            </div>
            <div className="text-lg font-bold">
              {startDate.getDate().toString()}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-sm truncate">{event.title}</h4>
              <Badge variant="secondary" className={cn("text-xs", eventConfig.color)}>
                {eventConfig.label}
              </Badge>
            </div>
            
            {/* Créateur de l'événement */}
            {creator && (
              <PersonName
                name={`Par ${creator.profiles?.first_name || creator.child_name || 'Inconnu'}`}
                avatarUrl={creator.profiles?.avatar_url || creator.child_avatar_url}
                size="sm"
                isChild={!!creator.child_name}
              />
            )}
            
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })} - {endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </div>
              {event.location && (
                <div className="flex items-center gap-1 truncate">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate">{event.location}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions Menu */}
          {showActions && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleEdit}>
                    <Edit className="h-4 w-4 mr-2" />
                    Modifier
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDeleteClick} className="text-destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer l'événement "{event.title}" ? 
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <>
      <Card className="cursor-pointer border border-panel bg-panel hover:bg-panel-hover transition-all relative group" onClick={handleEventClick}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-lg">{event.title}</h3>
            <div className="flex items-center gap-2">
              <Badge className={eventConfig.color}>
                {eventConfig.label}
              </Badge>
              {/* Actions Menu */}
              {showActions && (
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleEdit}>
                        <Edit className="h-4 w-4 mr-2" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDeleteClick} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            {/* Créateur de l'événement */}
            {creator && (
              <div className="mb-3">
                <PersonName
                  name={`Créé par ${creator.profiles?.first_name || creator.child_name || 'Inconnu'}`}
                  avatarUrl={creator.profiles?.avatar_url || creator.child_avatar_url}
                  size="md"
                  isChild={!!creator.child_name}
                />
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              <span>
                {startDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                {startDate.toDateString() !== endDate.toDateString() && (
                  <> - {endDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</>
                )}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>
                {startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })} - {endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })}
                {startDate.toDateString() !== endDate.toDateString() && (
                  <> ({endDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })})</>
                )}
              </span>
            </div>

            {event.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{event.location}</span>
              </div>
            )}

            {event.description && (
              <p className="mt-2 text-sm">{event.description}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer l'événement "{event.title}" ? 
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};